import CardCommon from '@op/components/CardCommon';
import { tableIcon, lineIcon, barIcon } from '@op/constants/chart';
import React, { useEffect, useMemo, useState } from 'react';
import { steamOptions, SteamOptionsType } from '../../constants';
import { Segment, Select } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import SteamTable from './SteamTable';
import SteamCharts from './SteamCharts';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import ChannelLabel from '@op/components/ChannelLabel';
import useCompareKeyMetricsColumns from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';

export default function Steam({
  filter,
  gameData,
  wholeGameInfo,
}: {
  filter: any;
  gameData: any;
  wholeGameInfo: any;
}) {
  const { formatOptionsMessage, formatMessage } = useLanguage();
  const [value, setValue] = useState<SteamOptionsType>('all_reviews');
  const [chartType, setChartType] = useState('line');
  const [selectValue, setSelectValue] = useState({ value: '' });
  const [options, setOptions] = useState<
    Array<{ text: string; value: string }>
  >([]);

  const { downloadLegacy, downloading } = useDownloadParams();

  const { columns: steamSelectOption } = useCompareKeyMetricsColumns({
    oneKey: 'Steam',
    twoKey: value,
    gameData,
    filter,
  });

  useEffect(() => {
    const list: any =
      steamSelectOption
        .filter((val) => val.key !== 'game')
        .map((val) => ({
          text: val.text,
          value: val?.select || val?.key,
          ...val,
        })) || [];
    setOptions(list);
    setSelectValue({ value: list?.[0]?.value || '' });
  }, [steamSelectOption, value]);

  const subType = useMemo(
    () => (selectValue.value === value ? '' : `${selectValue.value}`),
    [selectValue.value, value],
  );

  return (
    <CardCommon
      title={
        <div className="dbo-card-title-flex">
          <ChannelLabel channel_name={'steam'} showText={false}></ChannelLabel>
          <span>Steam</span>
        </div>
      }
      id="Steam"
      className="scroll-margin-top-target"
    >
      <CardCommon
        title={
          <>
            <Segment
              value={value}
              onChange={(value: SteamOptionsType) => {
                setValue(value);
                buriedBtnPoint('CKSteam_ReviewsMetricsTAB', value);
              }}
              options={formatOptionsMessage(steamOptions)}
            />
            <Select
              appearance="button"
              options={formatOptionsMessage(options)}
              value={selectValue.value}
              style={{ width: 192, fontWeight: 400 }}
              listWidth={192}
              onChange={(value: string) => {
                setSelectValue({ value });
                buriedBtnPoint('CKSteam_SELECT', value);
              }}
            ></Select>
          </>
        }
        hasDownload
        contentCard
        downloadLoading={downloading}
        operations={[tableIcon, lineIcon, barIcon]}
        defaultOperation={chartType}
        handleOperationsOnClick={(item) => {
          setChartType(item?.symbol || 'line');
        }}
        handleDownloadOnClick={() => {
          const downloadLimit = 10000;

          const downLoadParams = {
            ...filter,
            stats_types: ['game_store'],
            tab_type: 'steam',
            by_type: value,
            ...(subType ? { sub_type: `${value}_${subType}` } : {}),
            gameData,
          };
          const gameName = wholeGameInfo?.entity_name || '';
          const fileName = `${gameName} Compare Key Metrics - DataBrain Opinions`;
          downloadLegacy({
            limit: downloadLimit,
            fileName,
            params: downLoadParams,
            downloadFunc: 'getCompareKeyMetrics',
            sheetName: `Game Store - Steam`,
          });
        }}
        logBtnId="Steam Game Store"
      >
        <SteamCharts
          filter={filter}
          tab={value}
          gameData={gameData}
          chartType={chartType}
          subType={subType}
          options={options}
        />
        <SteamTable
          filter={filter}
          tab={value}
          gameData={gameData}
        ></SteamTable>
      </CardCommon>
    </CardCommon>
  );
}
